import type { HeroCollectionData } from '@beat-venues/common-types';
import { ContentfulContentImage } from '@beat-venues/contentful-image';
import {
    Block,
    BLOCKS,
    Document,
    Inline,
    Text,
    TopLevelBlock,
} from '@contentful/rich-text-types';
import { useTranslation } from 'next-i18next';
import { type VariantProps, tv } from 'tailwind-variants';

import { Button, Heading, RichText } from '../../atoms';
import type { CommonProps } from '../../types';
import { HeadingLayout } from '../heading-layout';

const truncateRichText = (document: Document, maxLength: number): Document => {
    let currentLength = 0;
    const truncatedContent = {
        ...document,
        content: document.content.reduce((acc: TopLevelBlock[], node) => {
            if (currentLength >= maxLength) return acc;

            if (node.nodeType === BLOCKS.PARAGRAPH) {
                const text = node.content.reduce((text, content) => {
                    if (content.nodeType === 'text') {
                        return text + content.value;
                    }
                    return text;
                }, '');

                if (currentLength + text.length <= maxLength) {
                    currentLength += text.length;
                    acc.push(node);
                } else {
                    const remainingLength = maxLength - currentLength;
                    const truncatedNode = {
                        ...node,
                        content: node.content.reduce(
                            (
                                textAcc: Array<Block | Inline | Text>,
                                content
                            ) => {
                                if (content.nodeType === 'text') {
                                    if (currentLength >= maxLength)
                                        return textAcc;
                                    const truncatedText = content.value.slice(
                                        0,
                                        remainingLength
                                    );
                                    currentLength += truncatedText.length;
                                    return [
                                        ...textAcc,
                                        {
                                            ...content,
                                            value: truncatedText + '...',
                                        },
                                    ];
                                }
                                return [...textAcc, content];
                            },
                            []
                        ),
                    };
                    acc.push(truncatedNode);
                }
            } else {
                acc.push(node);
            }
            return acc;
        }, []),
    };

    return truncatedContent;
};

export interface ShowcaseCollectionProps
    extends CommonProps,
        VariantProps<typeof ShowcaseCollectionStyles> {
    title?: string;
    description?: string;
    items: HeroCollectionData[];
    locale: string;
}

const ShowcaseCollectionStyles = tv({
    slots: {
        root: 'grid grid-cols-1 gap-x-4 gap-y-16 md:grid-cols-2',
        imageWrapper: 'mb-4 overflow-hidden rounded-md',
        content: 'xs:p-8',
    },
    variants: {
        size: {
            sm: {
                content: 'xs:py-6 flex flex-col gap-4',
            },
            lg: {
                content: 'grid-cols-5 flex flex-col sm:grid gap-8',
            },
        },
    },
});

const ShowcaseCollection = ({
    title,
    description,
    items,
    testId = 'showcase-collection',
    locale,
}: ShowcaseCollectionProps) => {
    const { root, imageWrapper, content } = ShowcaseCollectionStyles();

    const { t } = useTranslation('space');

    return (
        <HeadingLayout
            data-testid={testId}
            description={description}
            title={title}
        >
            <ul className={root()}>
                {items.map((showcase, index) => (
                    <li
                        key={showcase.title}
                        className={`click-area group ${
                            index === 0 && items.length % 2 !== 0
                                ? 'md:col-span-2'
                                : ''
                        }`}
                    >
                        <div className={imageWrapper()}>
                            <ContentfulContentImage
                                className="transition group-hover:scale-105"
                                data={showcase.heroImage}
                            />
                        </div>
                        <div
                            className={content({
                                size:
                                    index === 0 && items.length >= 3
                                        ? 'lg'
                                        : 'sm',
                            })}
                        >
                            <Heading as="h3" className="col-span-2 mb-0">
                                {showcase.title}
                            </Heading>
                            <div className="col-span-3 flex flex-col gap-4">
                                {showcase.content && (
                                    <RichText
                                        body={truncateRichText(
                                            showcase.content.fields.content,
                                            160
                                        )}
                                        locale={locale}
                                    />
                                )}
                                <Button
                                    className="click-area-link self-start"
                                    enableDrag={false}
                                    intent="primary"
                                    {...showcase.link}
                                >
                                    {t('showcase.moreInfo', { ns: 'space' })}
                                </Button>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </HeadingLayout>
    );
};

export { ShowcaseCollection };
